const custom_hostnames: { [index: string]: string } = {
  'commercial.keurig.com': 'Keurig',
  'jeux-video.fnac.com': 'FNAC',
  'groceries.asda.com': 'Asda',
  'catalog.11st.co.kr': '11st',
  'articulo.mercadolibre.com.mx': 'MercadoLibre',
  // Suning's modern URL is https://www.suning.com/...; we keep
  // product.suning.com here so legacy URLs (e.g. PDPs scraped under the
  // older subdomain) still infer correctly. The www form is handled by
  // the standard sourceTypes match.
  'product.suning.com': 'Suning',
  // JD's canonical PDP URL is https://item.jd.com/<id>.html (every row in
  // customer scopesheets uses this subdomain), but the brand base is
  // www.jd.com. The sourceTypes entry uses www so plain `jd.com` URLs
  // match; this explicit-host entry handles item.* which would otherwise
  // miss the longest-segment heuristic.
  'item.jd.com': 'JD',
  // Note: Following this pattern bc these sites exist in many TLDs
  GoogleMaps: 'GoogleMaps',
  GoogleShopping: 'GoogleShopping',
}

// Hosts where multiple sourceTypes entries share the hostname and differ only
// by URL path (e.g. tiktok.com/shop/ vs tiktok.com/shop/vn/). For these, resolve
// by longest path-prefix match. Hosts NOT in this set keep the existing
// first-hostname-match-wins behavior.
const PATH_AWARE_HOSTS: ReadonlySet<string> = new Set(['tiktok.com'])

// Ensure path ends with `/` so prefix matching is segment-aware. Without
// this, `/shop`.startsWith(`/shop/`) is false — so a URL like
// `tiktok.com/shop/vn` (country page, no trailing slash) would miss its
// `TikTokShopVietnam` variant whose base path is `/shop/vn/`.
const normalizePath = (path: string): string =>
  path.endsWith('/') ? path : path + '/'

const findSourceType = (
  sourceUrlType: string,
  sourceTypes: Record<string, string>,
  leaveUrlBaseOnly: boolean = false,
): string | undefined => {
  const sourceType = Object.keys(sourceTypes).find(
    (key) =>
      String(
        clearUrlType(sourceTypes[key] ?? '', leaveUrlBaseOnly),
      ).toLocaleLowerCase() === sourceUrlType,
  )
  return sourceType
}

const findSourceTypeByPathPrefix = (
  url: string,
  sourceTypes: Record<string, string>,
  leaveUrlBaseOnly: boolean,
): string | undefined => {
  let parsedUrl: URL
  try {
    parsedUrl = new URL(prepareURL(url))
  } catch {
    return undefined
  }
  const urlHost = parsedUrl.host.replace('www.', '').toLowerCase()
  const urlPath = normalizePath((parsedUrl.pathname || '/').toLowerCase())

  let bestName: string | undefined
  let bestLen = -1
  for (const [sourceName, baseUrl] of Object.entries(sourceTypes)) {
    try {
      const parsedBase = new URL(prepareURL(baseUrl ?? ''))
      const baseHost = parsedBase.host.replace('www.', '').toLowerCase()
      const basePath = normalizePath((parsedBase.pathname || '/').toLowerCase())

      let hostMatch: boolean
      if (leaveUrlBaseOnly) {
        const baseKey = baseHost
          .replace('.com', '')
          .split('.')
          .reduce((a, b) => (a.length >= b.length ? a : b))
        const urlKey = urlHost
          .replace('.com', '')
          .split('.')
          .reduce((a, b) => (a.length >= b.length ? a : b))
        hostMatch = urlKey === baseKey
      } else {
        hostMatch = urlHost === baseHost
      }

      if (!hostMatch || !urlPath.startsWith(basePath)) {
        continue
      }
      if (basePath.length > bestLen) {
        bestName = sourceName
        bestLen = basePath.length
      }
    } catch {
      continue
    }
  }
  return bestName
}

const processUrl = (
  url: string,
  sourceTypes: Record<string, string>,
  leaveUrlBaseOnly: boolean,
): string | undefined => {
  const sourceUrlType = clearUrlType(url, leaveUrlBaseOnly)
  if (sourceUrlType && sourceUrlType in custom_hostnames) {
    return custom_hostnames[sourceUrlType]
  }
  if (sourceUrlType && PATH_AWARE_HOSTS.has(sourceUrlType)) {
    return findSourceTypeByPathPrefix(url, sourceTypes, leaveUrlBaseOnly)
  }

  return (
    (sourceUrlType &&
      findSourceType(sourceUrlType, sourceTypes, leaveUrlBaseOnly)) ||
    undefined
  )
}

const prepareURL = (urlSrc: string): string => {
  let url = urlSrc
  url =
    !url.includes('https://') && !url.includes('http://')
      ? `https://${url}`
      : url
  url = url.endsWith('/') ? url : `${url}/`
  return url
}

const doesUrlContainSubstrings = (url: string, substrings: string[]): boolean =>
  [...substrings].every((subStr) => url.toLowerCase().includes(subStr))

const clearUrlType = (
  sourceUrl: string,
  leaveUrlBaseOnly: boolean = false,
): string | undefined => {
  const cleanedUrl = prepareURL(sourceUrl)

  try {
    const isGoogleShoppingURL = doesUrlContainSubstrings(cleanedUrl, [
      'www.google',
      '/shopping/',
    ])
    const isGoogleMapsURL = doesUrlContainSubstrings(cleanedUrl, [
      'www.google',
      '/maps/',
    ])
    if (isGoogleMapsURL) {
      return 'GoogleMaps'
    }
    if (isGoogleShoppingURL) {
      return 'GoogleShopping'
    }

    let host = new URL(cleanedUrl).host
    host = host.replace('www.', '')

    if (leaveUrlBaseOnly) {
      host = host
        .replace('.com', '')
        .split('.')
        .reduce((a, b) => (a.length >= b.length ? a : b))
    }
    return host
  } catch {
    return undefined
  }
}

export const getSourceTypeByUrl = (
  url: string,
  sourceTypes: Record<string, string>,
): string | undefined => {
  let sourceType = processUrl(url, sourceTypes, false)
  if (!sourceType) {
    sourceType = processUrl(url, sourceTypes, true)
  }
  return sourceType
}
